import { useState } from "react"
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { isEnabled } from "lib/@components/feature"
import Iconify from "minimals-template/components/Iconify"
import { FEATURE_FLAG_PROMOTE_REGIME } from "routes/optimiser/feature-flag-promote-regime"
import { getCurrencySymbol } from "../format-utils"
import { CostFigure } from "./cost-figure"

export function VariantPanelItem({ item }) {
    const {
        selectedVariantId,
        onSelectVariant,
        onEditVariant,
        onRemoveVariant,
        onRecalculateVariant,
        onPromoteVariant,
        canRemoveVariant,
    } = useBoundContext()
    const [menuAnchor, setMenuAnchor] = useState(null)
    const canPromote = Boolean(onPromoteVariant && isEnabled(FEATURE_FLAG_PROMOTE_REGIME))
    const hasMenuActions = Boolean(onEditVariant || onRecalculateVariant || canPromote || onRemoveVariant)
    const selected = item.id === selectedVariantId
    const isCalculating = item.status === "calculating"
    const costSymbol = getCurrencySymbol(item.rateCardCurrency)
    const removeIconColor = getRemoveIconColor()
    const selectedBorderColor = getSelectedBorderColor()
    const selectedBackgroundColor = getSelectedBackgroundColor()
    const hoverBackgroundColor = getHoverBackgroundColor()

    return (
        <ListItemBox
            data-cy="variant-panel-item"
            onClick={handleSelect}
            sx={{
                px: 1.5,
                py: 1.5,
                cursor: "pointer",
                borderRadius: 1,
                border: "1px solid",
                borderColor: selectedBorderColor,
                bgcolor: selectedBackgroundColor,
                "&:hover": { bgcolor: hoverBackgroundColor },
                gap: 1,
                alignItems: "flex-start",
            }}
        >
            <Box sx={{ width: 24, pt: 0.25, display: "flex", justifyContent: "center" }}>
                <If value={selected}>
                    <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: "primary.main", fontSize: 20 }} />
                </If>
            </Box>
            <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.75}>
                <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {item.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Iconify icon="solar:user-id-broken" sx={{ fontSize: 16, color: "text.primary" }} />
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {item.rateCardName || "No rate card"}
                    </Typography>
                    <If value={item.rateCardIsDeleted}>
                        <Iconify icon="mdi:alert-circle" sx={{ color: "error.main", fontSize: 16 }} />
                    </If>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Iconify icon="tabler:filter-cog" sx={{ fontSize: 16, color: "text.primary" }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {formatRuleSetNames()}
                    </Typography>
                </Stack>
                <If value={item.annualCost != null}>
                    <CostFigure
                        value={item.annualCost}
                        currencySymbol={costSymbol}
                        variant="subtitle1"
                        fontWeight={700}
                    />
                </If>
            </Stack>
            <If value={hasMenuActions}>
                <Box sx={{ width: 36, display: "flex", justifyContent: "center" }}>
                    <IconButton
                        aria-label="More options"
                        data-cy="variant-panel-menu-btn"
                        size="small"
                        onClick={openMenu}
                    >
                        <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                        <If value={onEditVariant}>
                            <MenuItem data-cy="variant-panel-menu-edit" onClick={handleEdit}>
                                <ListItemIcon>
                                    <Iconify icon="solar:pen-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                        </If>
                        <If value={onRecalculateVariant}>
                            <MenuItem
                                data-cy="variant-panel-menu-recalculate"
                                onClick={handleRecalculate}
                                disabled={isCalculating}
                            >
                                <ListItemIcon>
                                    <Iconify icon="mdi:refresh" width={20} />
                                </ListItemIcon>
                                <ListItemText>Recalculate</ListItemText>
                            </MenuItem>
                        </If>
                        <If value={canPromote}>
                            <MenuItem data-cy="variant-panel-menu-promote" onClick={handlePromote}>
                                <ListItemIcon>
                                    <Iconify icon="solar:square-arrow-right-up-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Promote to Draft Regime</ListItemText>
                            </MenuItem>
                        </If>
                        <If value={onRemoveVariant}>
                            <MenuItem
                                data-cy="variant-panel-menu-remove"
                                onClick={handleRemove}
                                disabled={!canRemoveVariant}
                            >
                                <ListItemIcon>
                                    <Iconify icon="solar:trash-bin-trash-bold" width={20} color={removeIconColor} />
                                </ListItemIcon>
                                <ListItemText sx={{ color: removeIconColor }}>Remove</ListItemText>
                            </MenuItem>
                        </If>
                    </Menu>
                </Box>
            </If>
        </ListItemBox>
    )

    function formatRuleSetNames() {
        let names = []
        if (item.ruleSetNames?.length) {
            names = item.ruleSetNames
        } else if (item.ruleSetName) {
            names = [item.ruleSetName]
        }
        if (!names.length) return "No rules"
        return names.join(", ")
    }

    function getRemoveIconColor() {
        if (canRemoveVariant) return "error.main"
        return "text.disabled"
    }

    function getSelectedBorderColor() {
        if (selected) return "primary.main"
        return "divider"
    }

    function getSelectedBackgroundColor() {
        if (selected) return "#DEF2F4"
        return "transparent"
    }

    function getHoverBackgroundColor() {
        if (selected) return "#DEF2F4"
        return "action.hover"
    }

    function handleSelect() {
        onSelectVariant?.(item.id)
    }

    function openMenu(e) {
        e.stopPropagation()
        setMenuAnchor(e.currentTarget)
    }

    function closeMenu(e) {
        if (e) e.stopPropagation()
        setMenuAnchor(null)
    }

    function handleEdit(e) {
        e.stopPropagation()
        closeMenu()
        onEditVariant?.(item)
    }

    function handleRecalculate(e) {
        e.stopPropagation()
        closeMenu()
        onRecalculateVariant?.(item.id)
    }

    function handleRemove(e) {
        e.stopPropagation()
        closeMenu()
        onRemoveVariant?.(item.id)
    }

    function handlePromote(e) {
        e.stopPropagation()
        closeMenu()
        onPromoteVariant?.(item)
    }
}
